// Shared, pure UI helpers for the Projects pages: label maps, the status badge style,
// and the client-side BSR / Section 20 preview that MIRRORS the server rules (the server
// is authoritative on create; this only drives the live warning banners as the office
// fills the form). Kept pure so the flag previews and the status style are unit tested.
import type { ProjectStatus, WorksClassification, ProjectSubcontractorStatus } from "../../lib/api";

// The single project taxonomy (Session AAA Item 2: the separate TYPE field was removed).
// WORKS_TYPE_OPTIONS is the SELECTABLE list in the Director's display order; the label
// map also covers the legacy 'modification' value so existing rows still display.
export const WORKS_TYPE_OPTIONS: WorksClassification[] = [
  "modernisation",
  "installation",
  "repair",
  "condition_report",
  "post_loler_remedial",
];

export const WORKS_TYPE_LABELS: Record<WorksClassification, string> = {
  modernisation: "Modernisation",
  installation: "New installation",
  repair: "Repair",
  condition_report: "Condition report",
  post_loler_remedial: "Post-LOLER remedial works",
  modification: "Modification", // legacy value, display only (no longer selectable)
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  enquiry: "Enquiry",
  surveying: "Surveying",
  quoting: "Quoting",
  quoted: "Quoted",
  accepted: "Accepted",
  works_scheduled: "Works scheduled",
  in_progress: "In progress",
  on_hold: "On hold",
  complete: "Complete",
  invoiced: "Invoiced",
  lost: "Lost",
  cancelled: "Cancelled",
};

export const SUBCONTRACTOR_STATUS_LABELS: Record<ProjectSubcontractorStatus, string> = {
  proposed: "Proposed",
  agreed: "Scope agreed",
  agreement_sent: "Agreement sent",
  agreement_signed: "Agreement signed",
  on_site: "On site",
  complete: "Complete",
};

// The happy-path lifecycle stages, in order, for the header progress indicator (quotation-flow
// redesign). The off-path statuses (on_hold / lost / cancelled) are not stages on the bar; the
// indicator shows the current status as a label instead. Pure -> unit tested.
export const PROJECT_STAGE_FLOW: ProjectStatus[] = [
  "enquiry",
  "surveying",
  "quoting",
  "quoted",
  "accepted",
  "works_scheduled",
  "in_progress",
  "complete",
  "invoiced",
];

export const OFF_FLOW_STATUSES: ProjectStatus[] = ["on_hold", "lost", "cancelled"];

// The index of the current status within the happy-path flow, or -1 when it is off-path.
export function projectStageIndex(status: ProjectStatus): number {
  return PROJECT_STAGE_FLOW.indexOf(status);
}

// Session AS, Item 5: is moving from `current` to `target` a step BACKWARD along the happy-path
// flow? True only when both are on the flow and the target sits earlier, so the interactive progress
// bar can confirm a backward move (which may be accidental) but let a forward move through directly.
// An off-path move (e.g. resuming from on_hold) is not "backward". Pure -> unit tested.
export function isBackwardStage(current: ProjectStatus, target: ProjectStatus): boolean {
  const c = projectStageIndex(current);
  const t = projectStageIndex(target);
  return c >= 0 && t >= 0 && t < c;
}

// The ProjectDetailPage's four workflow stages (Session AAB redesign): Details (the job, the
// client, the survey), Costing (the internal price build), Quote (scope -> approve -> send) and
// Delivery (subcontractors, invoicing, programme, closeout documents).
export type ProjectDetailTab = "details" | "costs" | "quote" | "delivery";

// The most relevant tab to open for a lifecycle stage (Session AS, Item 5; remapped to the
// Session AAB four-stage structure). null keeps the current tab. Pure -> unit tested.
export function stageTabFor(status: ProjectStatus): ProjectDetailTab | null {
  switch (status) {
    case "surveying":
      return "details";
    case "quoting":
    case "quoted":
      return "quote";
    case "accepted":
    case "works_scheduled":
    case "in_progress":
    case "complete":
      return "delivery";
    default:
      return null;
  }
}

// ---- The next-step banner (Session AAB, the heart of the redesign) ------------------------
// One computed "what to do next" for the project page, evaluated in priority order from the
// project's own state, so the interface drives the workflow instead of the user's memory of
// which tab to visit. Pure -> exhaustively unit tested.

export interface NextActionInput {
  status: ProjectStatus;
  archivedAt: string | null;
  customerId: string | null;
  clientEmail: string | null;
  contractValue: number | null;
  scopeSummary: string | null;
  hasQuotation: boolean;
  // The server's REAL-envelope flag: a Signable envelope is out and unsigned. A
  // crash-orphaned pending sentinel counts as hasQuotation but NOT as outstanding,
  // so the banner and the send button always agree (Session AAX, Loop 1).
  quotationOutstanding: boolean;
  quotationSignedAt: string | null;
  bsrRequired: boolean;
  bsrCleared: boolean;
}

// The section the CTA lands on within its tab (Session AAX, Loop 2): "client" opens the
// client edit modal directly; the rest scroll to (and briefly highlight) the named section,
// so following the one green button never needs a hunt within the tab either.
export type ProjectFocusSection = "client" | "costing-lines" | "quote-scope" | "quote-send" | "delivery-invoicing";

export interface NextAction {
  key:
    | "add_client"
    | "enter_costs"
    | "generate_scope"
    | "send_quote"
    | "awaiting_signature"
    | "bsr_gate"
    | "raise_invoice"
    | "deliver"
    | "closeout";
  label: string; // the one-sentence instruction
  hint: string; // one supporting sentence of plain-English context
  tab: ProjectDetailTab | null; // where the CTA takes the user (null: no navigation needed)
  section: ProjectFocusSection | null; // the section within that tab the CTA lands on
  cta: string | null; // the CTA button label (null: no button)
  tone: "action" | "waiting"; // green do-this vs amber waiting-on-someone-else
}

export function nextProjectAction(p: NextActionInput): NextAction | null {
  // An archived or off-path project shows its own state (the archived badge / the off-flow
  // note); a finished project needs nothing.
  if (p.archivedAt != null) return null;
  if ((OFF_FLOW_STATUSES as string[]).includes(p.status)) return null;
  if (p.status === "invoiced") return null;

  const signed = p.quotationSignedAt != null;
  // Session AAX Loop 1: the banner derives from the ACTUAL pipeline stage AND the envelope
  // state. Once the project sits at accepted or beyond, the quotation chapter is over --
  // whether it was signed via Signable or the office deliberately advanced past it (the
  // audited override) -- so the "waiting for signature" banner can never go stale there.
  const postAcceptance = projectStageIndex(p.status) >= projectStageIndex("accepted");

  if (!postAcceptance) {
    if (signed) {
      // Signed but the status was never advanced (the forward-only webhook can leave a
      // late-signed project at quoted or earlier): the next step is billing, never a re-send.
      return raiseInvoiceAction(signed);
    }
    if (p.quotationOutstanding) {
      // Genuinely awaiting signature: a REAL unsigned envelope is out with the client.
      return {
        key: "awaiting_signature",
        label: "Waiting for the client to sign the quotation.",
        hint: "Signable reminds them automatically. You can re-send or preview it from the Quote tab.",
        tab: "quote",
        section: null,
        cta: null,
        tone: "waiting",
      };
    }
    // Pre-send: walk the pipeline gaps in the order the office fills them. (A crash-orphaned
    // send sentinel lands here too: the Quote tab still shows the normal Send button, so the
    // next step is truthfully "send".)
    if (!p.customerId && !p.clientEmail?.trim()) {
      return {
        key: "add_client",
        label: "Add the client's contact details.",
        hint: "The quotation cannot be sent until the project has a client email or a linked customer.",
        tab: "details",
        section: "client",
        cta: "Add client details",
        tone: "action",
      };
    }
    if (p.contractValue == null) {
      return {
        key: "enter_costs",
        label: "Enter the costs to price this project.",
        hint: "Add your labour, supplier and subcontractor costs. The project value is calculated from cost plus markup.",
        tab: "costs",
        section: "costing-lines",
        cta: "Enter costs",
        tone: "action",
      };
    }
    if (!p.scopeSummary?.trim()) {
      return {
        key: "generate_scope",
        label: "Generate the scope of works.",
        hint: "One click drafts it from the survey notes and any supplier quotes. Review it, then approve it for the quotation.",
        tab: "quote",
        section: "quote-scope",
        cta: "Generate scope",
        tone: "action",
      };
    }
    return {
      key: "send_quote",
      label: "The quotation is ready. Send it to the client for signature.",
      hint: "Preview the PDF if you want, then send it via Signable. Signing it forms a binding agreement.",
      tab: "quote",
      section: "quote-send",
      cta: "Send the quotation",
      tone: "action",
    };
  }

  // Post-acceptance: the delivery steps, driven by the stage alone.
  if (p.status === "accepted" && p.bsrRequired && !p.bsrCleared) {
    return {
      key: "bsr_gate",
      label: "Record the BSR Gateway 2 approval date before scheduling works.",
      hint: "This is a Higher-Risk Building: enter the approval date in the red banner above to lift the block.",
      tab: null,
      section: null,
      cta: null,
      tone: "action",
    };
  }
  if (p.status === "complete") {
    return {
      key: "closeout",
      label: "Send the completion report and raise the final invoice.",
      hint: "Both live on the Delivery tab. Mark the project invoiced when the final invoice is raised.",
      tab: "delivery",
      section: "delivery-invoicing",
      cta: "Open delivery",
      tone: "action",
    };
  }
  if (p.status === "works_scheduled" || p.status === "in_progress") {
    return {
      key: "deliver",
      label: "Raise stage invoices as they fall due and record completion when the works finish.",
      hint: "Subcontractor agreements, invoicing and the programme live on the Delivery tab.",
      tab: "delivery",
      section: "delivery-invoicing",
      cta: "Open delivery",
      tone: "action",
    };
  }
  // Accepted and clear to proceed.
  return raiseInvoiceAction(signed);
}

// The raise-the-first-invoice step. The label only claims a signature when one is actually
// recorded: a project moved to accepted via the audited manual override is unsigned, and the
// banner must never assert otherwise (Session AAX, Loop 1).
function raiseInvoiceAction(signed: boolean): NextAction {
  return {
    key: "raise_invoice",
    label: signed
      ? "The client has signed. Raise the first stage invoice and schedule the works."
      : "Raise the first stage invoice and schedule the works.",
    hint: "Raise the payment-with-order invoice on the Delivery tab, then move the project to Works scheduled.",
    tab: "delivery",
    section: "delivery-invoicing",
    cta: "Open delivery",
    tone: "action",
  };
}

// HRB preview (mirrors the server isHigherRiskBuilding): 18m+ OR 7+ storeys, AND 2+
// residential units. Strings come from form inputs.
export function previewHrb(input: { buildingHeightM?: string | number | null; buildingStoreys?: string | number | null; residentialUnits?: string | number | null }): boolean {
  const n = (v: string | number | null | undefined): number => {
    if (v == null || v === "") return 0;
    const x = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };
  const tallEnough = n(input.buildingHeightM) >= 18 || n(input.buildingStoreys) >= 7;
  return tallEnough && n(input.residentialUnits) >= 2;
}

// Section 20 preview (mirrors the server isSection20Required): any one leaseholder
// charged more than 250 pounds.
export function previewSection20(leaseholderCharge?: string | number | null): boolean {
  if (leaseholderCharge == null || leaseholderCharge === "") return false;
  const x = typeof leaseholderCharge === "number" ? leaseholderCharge : parseFloat(leaseholderCharge);
  return Number.isFinite(x) && x > 250;
}

// The applicable standard suggested from the works classification (Session AU, Item 1). Used to
// pre-fill the Scope/Commercials applicable-standard field when the office picks a classification
// and the field is still blank. The office can override it. Pure -> unit tested. Mirrors the intent
// of the server's defaultApplicableStandard(type), keyed on the works classification instead.
export function standardForWorksClass(cls: WorksClassification | ""): string {
  switch (cls) {
    case "installation":
      return "BS EN 81-20 and BS EN 81-50 (new lift installation)";
    case "modernisation":
    case "modification": // legacy value, same standard as a modernisation
      return "BS EN 81-80 (modernisation of existing lifts)";
    case "repair":
    case "post_loler_remedial":
      return "BS EN 81-20 and BS EN 81-50";
    default:
      return ""; // condition_report / not set: no single standard applies
  }
}

export function gbp(n: number | null | undefined): string {
  if (n == null) return "-";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Session ABN (Item 1): whether the amber "reopened" banner shows. Visible while the project
// has been reopened and the quotation has NOT been re-sent since the reopen: quotationSentAt
// missing, unparseable, or at/before reopenedAt all keep the banner up (only a genuinely LATER
// send clears it). Pure -> unit tested.
export function reopenBannerVisible(reopenedAt: string | null | undefined, quotationSentAt: string | null | undefined): boolean {
  if (!reopenedAt) return false;
  const reopened = new Date(reopenedAt).getTime();
  if (Number.isNaN(reopened)) return false;
  if (!quotationSentAt) return true;
  const sent = new Date(quotationSentAt).getTime();
  if (Number.isNaN(sent)) return true;
  return sent <= reopened;
}

// A human file size for a document's byte count (Session AS, Item 4). Pure -> unit tested.
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
